import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import {
  Card,
  Form,
  Input,
  Select,
  DatePicker,
  Upload,
  Button,
  Table,
  Tag,
  Image,
  message,
  Switch,
  Tabs,
  Space,
  Typography,
  Divider,
  Popconfirm,
  Modal,
} from 'antd';
import {
  BellOutlined,
  SendOutlined,
  ReloadOutlined,
  HistoryOutlined,
  PlusOutlined,
  AppstoreOutlined,
  TagOutlined,
  ClockCircleOutlined,
  LinkOutlined,
  EditOutlined,
  DeleteOutlined,
  BugOutlined,
  SearchOutlined,
  FileImageOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';

import config from '../../config';
import { notificationCreate, notificationList, notificationUpdate, notificationDelete, notificationTest } from '../../modules/notification';
import { shortenLinkList } from '../../modules/shortenLink';
import CampaignApi from '../../modules/api/campaignApi';
import tokenApi from '../../modules/api/tokenApi';

import './Notifications.scss';

const { TextArea, Search } = Input;
const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { RangePicker } = DatePicker;

// Component to load images with Authorization header to bypass gateway 401
const ImageWithAuth = ({ src, ...props }) => {
  const [imgData, setImgData] = React.useState(null);
  const [error, setError] = React.useState(false);

  React.useEffect(() => {
    if (!src) return;

    // If it's already a blob or data URL, use it directly
    if (src.startsWith('blob:') || src.startsWith('data:')) {
      setImgData(src);
      return;
    }

    const fetchImage = async () => {
      try {
        const response = await axios.get(src, {
          headers: { Authorization: tokenApi.GetToken() },
          responseType: 'blob'
        });
        const url = URL.createObjectURL(response.data);
        setImgData(url);
      } catch (err) {
        console.error('Failed to fetch image with auth:', err);
        setError(true);
      }
    };

    fetchImage();

    return () => {
      if (imgData && imgData.startsWith('blob:')) {
        URL.revokeObjectURL(imgData);
      }
    };
  }, [src]);

  if (error) return <div className="no-media">Error</div>;
  if (!imgData) return <ReloadOutlined spin />;

  return <Image {...props} src={imgData} />;
};

class Notifications extends Component {
  constructor(props) {
    super(props);
    this.formRef = React.createRef();
    this.state = {
      loading: false,
      submitting: false,
      notifications: [],
      imageUrl: '',
      uploadFileList: [],
      imageUploading: false,
      activeTab: '1',
      editingId: null,
      filters: {
        search: '',
        appId: undefined,
        status: undefined,
        range: null,
      },
      testModalVisible: false,
      testToken: '',
      testing: false,
    };
  }

  componentDidMount() {
    this.fetchNotifications();
    this.props.shortenLinkList(config.accountId);
  }

  fetchNotifications = () => {
    const { filters } = this.state;
    this.setState({ loading: true });

    const query = {
      search: filters.search,
      appId: filters.appId,
      status: filters.status,
      startDate: filters.range?.[0]?.toISOString(),
      endDate: filters.range?.[1]?.toISOString(),
    };

    this.props.notificationList(query).then((data) => {
      this.setState({ notifications: data, loading: false });
    }).catch(() => {
      message.error('Failed to load notifications');
      this.setState({ loading: false });
    });
  };

  handleFilterChange = (key, value) => {
    this.setState(prevState => ({
      filters: {
        ...prevState.filters,
        [key]: value
      }
    }), () => {
      if (key !== 'search') this.fetchNotifications();
    });
  };

  handleSearch = (value) => {
    this.handleFilterChange('search', value);
    this.fetchNotifications();
  };

  handleImageUpload = ({ file, onSuccess, onError }) => {
    const reader = new FileReader();
    const actualFile = file.originFileObj || file;

    reader.readAsDataURL(actualFile);
    reader.onload = () => {
      const base64Image = reader.result;

      this.setState({ imageUploading: true });

      // Use JSON/Base64 to bypass proxy issues with multipart/form-data
      axios.post(
        `${CampaignApi.getBaseURL()}/notification/image`,
        { image: base64Image }, // Send as JSON field
        {
          headers: {
            Authorization: tokenApi.GetToken(),
            'Content-Type': 'application/json'
          },
        }
      ).then((response) => {
        const imagePath = response.data.path;
        // Build URL by removing /service/campaign from the base URL if it exists
        // Also be careful not to double up /v1 if it exists in both base and path
        const base = CampaignApi.getBaseURL().replace('/service/campaign', '');
        const displayUrl = response.data.preview || (imagePath.startsWith('/v1/') && base.endsWith('/v1')
          ? `${base.replace('/v1', '')}${imagePath}`
          : `${base}${imagePath}`);
        const publicUrl = response.data.preview || displayUrl;

        this.setState({
          imageUrl: publicUrl,
          uploadFileList: [{
            uid: file.uid,
            name: file.name,
            status: 'done',
            url: displayUrl,
          }],
        });
        onSuccess(response.data, file);
      }).catch((error) => {
        console.error('Image upload failed:', error);
        const errorMsg = error.response?.data?.message || error.response?.data?.error || 'Failed to upload image';
        message.error(errorMsg);
        onError(error);
      }).finally(() => {
        this.setState({ imageUploading: false });
      });
    };
    reader.onerror = (error) => {
      console.error('FileReader error:', error);
      message.error('Failed to read image file');
      onError(error);
    };
  };

  handleRemoveImage = () => {
    this.setState({ imageUrl: '', uploadFileList: [] });
  };

  handleEdit = (record) => {
    const { title, body, appId, env, topics, scheduleAt, imageUrl, deepLink, id, status, isSilent, data } = record;
    const isSent = status === 'sent' || status === 'sending';
    const isScheduled = status === 'scheduled';

    const base = CampaignApi.getBaseURL().replace('/service/campaign', '');
    const displayUrl = imageUrl && (typeof imageUrl === 'string' && imageUrl.startsWith('/'))
      ? (imageUrl.startsWith('/v1/') && base.endsWith('/v1')
        ? `${base.replace('/v1', '')}${imageUrl}`
        : `${base}${imageUrl}`)
      : imageUrl;

    this.setState({
      editingId: isSent ? null : id, // If sent, treat as a new clone
      imageUrl: imageUrl || '',
      uploadFileList: imageUrl ? [{
        uid: '-1',
        name: 'Existing Image',
        status: 'done',
        url: displayUrl,
      }] : [],
      activeTab: '1'
    }, () => {
      // Exclude internal keys from the data object when presenting to the user
      const customData = { ...(data || {}) };
      delete customData.deepLink;

      this.formRef.current?.setFieldsValue({
        title,
        body,
        appId,
        env: env || (config.notificationEnvs && config.notificationEnvs[0]) || 'PROD',
        topics,
        isSilent: !!isSilent,
        customData: Object.keys(customData).length > 0 ? JSON.stringify(customData, null, 2) : '',
        sendNow: !isScheduled, // If it was scheduled, keep it as scheduled mode
        scheduleAt: scheduleAt ? dayjs(scheduleAt) : null,
        deepLink,
        campaignKey: null // Reset campaign selection on edit
      });
      if (isSent) {
        message.info('Loaded sent notification as a new draft');
      }
    });
  };

  handleCancelEdit = () => {
    this.setState({ editingId: null, imageUrl: '', uploadFileList: [] });
    this.formRef.current?.resetFields();
  };

  handleDelete = (id) => {
    this.props.notificationDelete(id).then(() => {
      message.success('Notification deleted');
      this.fetchNotifications();
    }).catch(() => {
      message.error('Failed to delete notification');
    });
  };

  handleTestSend = () => {
    const { testToken } = this.state;
    const values = this.formRef.current?.getFieldsValue();
    const { imageUrl } = this.state;

    if (!values.title || !values.body || !values.appId) {
      message.warning('Please fill in Application, Title, and Body first');
      return;
    }

    if (!testToken) {
      message.warning('Please enter a device token');
      return;
    }

    if (imageUrl && typeof imageUrl === 'string' && imageUrl.startsWith('data:')) {
      message.error('Image URL cannot be base64 data. Please upload the image and use the generated URL.');
      return;
    }

    let customData = {};
    if (values.customData) {
      try {
        customData = JSON.parse(values.customData);
      } catch (e) {
        message.error('Invalid Custom Data JSON');
        return;
      }
    }

    this.setState({ testing: true });
    const payload = {
      title: values.title,
      body: values.body,
      appId: values.appId,
      env: values.env || (config.notificationEnvs && config.notificationEnvs[0]) || 'PROD',
      imageUrl: imageUrl || null,
      isSilent: !!values.isSilent,
      deepLink: values.deepLink || null,
      data: customData,
      token: testToken
    };

    this.props.notificationTest(payload).then(() => {
      message.success('Test notification sent');
      this.setState({ testModalVisible: false, testToken: '' });
    }).catch((error) => {
      message.error(error.response?.data?.error || 'Failed to send test');
    }).finally(() => {
      this.setState({ testing: false });
    });
  };

  handleCampaignChange = (value) => {
    const { shortenLinks } = this.props;
    const selectedLink = shortenLinks.find(link => link.shortenKey === value);
    if (selectedLink) {
      this.formRef.current?.setFieldsValue({
        deepLink: selectedLink.shortenLink
      });
    }
  };

  handleDeepLinkChange = () => {
    this.formRef.current?.setFieldsValue({
      campaignKey: null
    });
  };

  handleSubmit = (values) => {
    const { editingId } = this.state;
    const { imageUrl } = this.state;
    const sendNow = values.sendNow !== false;

    if (imageUrl && typeof imageUrl === 'string' && imageUrl.startsWith('data:')) {
      message.error('Image URL cannot be base64 data. Please upload the image and use the generated URL.');
      return;
    }

    let customData = {};
    if (values.customData) {
      try {
        customData = JSON.parse(values.customData);
      } catch (e) {
        message.error('Invalid Custom Data JSON');
        return;
      }
    }

    const payload = {
      title: values.title,
      body: values.body,
      appId: values.appId,
      env: values.env || (config.notificationEnvs && config.notificationEnvs[0]) || 'PROD',
      topics: values.topics || [],
      scheduleAt: sendNow ? null : (values.scheduleAt ? values.scheduleAt.toISOString() : null),
      imageUrl: imageUrl || null,
      isSilent: !!values.isSilent,
      deepLink: values.deepLink || null,
      data: customData
    };

    this.setState({ submitting: true });

    const request = editingId
      ? this.props.notificationUpdate(editingId, payload)
      : this.props.notificationCreate(payload);

    request.then(() => {
      message.success(editingId ? 'Notification updated' : 'Notification queued');
      this.formRef.current?.resetFields?.();
      this.setState({
        imageUrl: '',
        uploadFileList: [],
        activeTab: '2',
        editingId: null
      });
      this.fetchNotifications();
    }).catch((error) => {
      const errorMsg = error.response?.data?.error || 'Failed to process notification';
      message.error(errorMsg);
    }).finally(() => {
      this.setState({ submitting: false });
    });
  };

  renderStatusTag = (status) => {
    const colorMap = {
      scheduled: 'blue',
      sending: 'gold',
      sent: 'green',
      failed: 'red',
    };
    const color = colorMap[status] || 'default';
    return <Tag color={color} style={{ borderRadius: '4px', textTransform: 'capitalize' }}>{status}</Tag>;
  };

  render() {
    const {
      loading, notifications, uploadFileList, imageUploading, submitting,
      activeTab, editingId, filters, testModalVisible, testToken, testing
    } = this.state;

    const columns = [
      {
        title: 'Notification Content',
        dataIndex: 'title',
        key: 'title',
        width: '35%',
        render: (value, record) => (
          <div className="notification-content-cell">
            <div className="title-row">
              <Text strong>{value || (record.isSilent ? '(Silent Notification)' : '-')}</Text>
              <div className="app-id-tag">{record.appId}</div>
              {record.isSilent && <Tag color="default" style={{ marginLeft: 8 }}>Silent</Tag>}
              {record.env && <Tag color="geekblue" style={{ marginLeft: 8 }}>{record.env}</Tag>}
            </div>
            <div className="body-text">{record.body || (record.isSilent ? 'No visual alert content' : '')}</div>
          </div>
        ),
      },
      {
        title: 'Target Topics',
        dataIndex: 'topics',
        key: 'topics',
        render: (value) => (
          <div className="topic-tags">
            {(value || []).map(topic => <Tag key={topic} color="processing">{topic}</Tag>)}
          </div>
        ),
      },
      {
        title: 'Schedule & Status',
        key: 'schedule_status',
        render: (_, record) => (
          <Space direction="vertical" size={2}>
            <div className="status-row">
              {this.renderStatusTag(record.status)}
              {record.sentAt && <span className="sent-time">{dayjs(record.sentAt).format('MMM D, HH:mm')}</span>}
            </div>
            {!record.sentAt && record.scheduleAt && (
              <span className="schedule-time">
                <ClockCircleOutlined /> {dayjs(record.scheduleAt).format('MMM D, HH:mm')}
              </span>
            )}
          </Space>
        )
      },
      {
        title: 'Rich Media',
        dataIndex: 'imageUrl',
        key: 'imageUrl',
        width: 100,
        render: (value) => {
          if (!value) return <div className="no-media">-</div>;

          // 'value' now contains the path: "/v1/media?file=xxx.png"
          // We use config.api directly to avoid the additional "/service/campaign" segment if possible
          const base = CampaignApi.getBaseURL().replace('/service/campaign', '');
          const displayUrl = (typeof value === 'string' && value.startsWith('/'))
            ? (value.startsWith('/v1/') && base.endsWith('/v1')
              ? `${base.replace('/v1', '')}${value}`
              : `${base}${value}`)
            : value;

          return <ImageWithAuth className="history-thumb" src={displayUrl} preview={true} />;
        },
      },
      {
        title: 'Deep Link',
        dataIndex: 'deepLink',
        key: 'deepLink',
        render: (value) => value ? (
          <div className="deeplink-cell">
            <LinkOutlined />
            <span title={value}>{value}</span>
          </div>
        ) : '-',
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 80,
        render: (_, record) => (
          <Space size="small">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => this.handleEdit(record)}
              title="Edit / Reuse Notification"
            />
            <Popconfirm
              title="Are you sure you want to delete this notification?"
              onConfirm={() => this.handleDelete(record.id)}
              okText="Yes"
              cancelText="No"
              placement="topRight"
            >
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                title="Delete Notification"
              />
            </Popconfirm>
          </Space>
        )
      }
    ];

    return (
      <div className="notification-container">
        <Card className="notification-card" bordered={false}>
          <div className="notification-header">
            <div className="title-group">
              <BellOutlined className="header-icon" />
              <div>
                <Title level={4} style={{ margin: 0 }}>Push Notifications</Title>
                <Text type="secondary">Create and manage your campaign messages</Text>
              </div>
            </div>
            {activeTab === '2' && (
              <Button
                icon={<ReloadOutlined />}
                onClick={this.fetchNotifications}
                loading={loading}
              >
                Refresh
              </Button>
            )}
          </div>

          <Tabs
            activeKey={activeTab}
            onChange={(key) => this.setState({ activeTab: key })}
            className="notification-tabs"
            type="line"
            size="large"
          >
            <TabPane
              tab={<span>{editingId ? <EditOutlined /> : <PlusOutlined />}{editingId ? 'Edit Campaign' : 'Composer'}</span>}
              key="1"
            >
              <div className="composer-container">
                <Form
                  layout="vertical"
                  ref={this.formRef}
                  onFinish={this.handleSubmit}
                  initialValues={{ sendNow: true }}
                  requiredMark="optional"
                >
                  <div className="form-section">
                    <div className="section-header">
                      <Title level={5}><AppstoreOutlined /> Destination</Title>
                    </div>
                    <div className="form-row-grid">
                      <Form.Item
                        label="Application"
                        name="appId"
                        rules={[{ required: true, message: 'Select an app ID' }]}
                      >
                        <Select
                          placeholder="Select application"
                          options={(config.notificationApps || []).map((appId) => ({ value: appId, label: appId }))}
                        />
                      </Form.Item>
                      <Form.Item
                        label="Environment"
                        name="env"
                        rules={[{ required: true, message: 'Select env' }]}
                        initialValue={(config.notificationEnvs && config.notificationEnvs[0]) || 'PROD'}
                      >
                        <Select
                          placeholder="Select env"
                          options={(config.notificationEnvs || ['PROD']).map(env => ({ value: env, label: env }))}
                        />
                      </Form.Item>
                      <Form.Item
                        label="Target Audiences (Topics)"
                        name="topics"
                        rules={[{ required: true, message: 'Add at least one topic' }]}
                      >
                        <Select
                          mode="tags"
                          placeholder="Search or add topics"
                          options={(config.notificationTopics || []).map((topic) => ({ value: topic, label: topic }))}
                          suffixIcon={<TagOutlined />}
                        />
                      </Form.Item>
                    </div>
                  </div>

                  <Divider />

                  <div className="form-section">
                    <div className="section-header">
                      <Title level={5}><BellOutlined /> Notification Content</Title>
                      <Form.Item name="isSilent" valuePropName="checked" noStyle>
                        <Switch
                          checkedChildren="Silent Mode ON"
                          unCheckedChildren="Silent Mode OFF"
                          style={{ marginLeft: 'auto' }}
                        />
                      </Form.Item>
                    </div>
                    <Form.Item shouldUpdate={(prev, curr) => prev.isSilent !== curr.isSilent} noStyle>
                      {({ getFieldValue }) => {
                        const isSilent = getFieldValue('isSilent') === true;
                        return (
                          <>
                            <Form.Item
                              label="Title"
                              name="title"
                              rules={[{ required: !isSilent, message: 'Title is required' }]}
                            >
                              <Input placeholder={isSilent ? "Optional internal title" : "What is the headline?"} maxLength={100} showCount />
                            </Form.Item>
                            <Form.Item
                              label="Message Body"
                              name="body"
                              rules={[{ required: !isSilent, message: 'Message is required' }]}
                            >
                              <TextArea rows={4} placeholder={isSilent ? "Optional internal description" : "Type your notification description here..."} maxLength={500} showCount />
                            </Form.Item>
                          </>
                        );
                      }}
                    </Form.Item>

                    <div className="form-row-grid items-3">
                      <Form.Item
                        label="Link Campaign"
                        name="campaignKey"
                      >
                        <Select
                          placeholder="Select campaign to get link"
                          onChange={this.handleCampaignChange}
                          allowClear
                          showSearch
                          optionFilterProp="children"
                        >
                          {(this.props.shortenLinks || []).map(link => (
                            <Select.Option key={link.shortenKey} value={link.shortenKey}>
                              {link.name}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                      <Form.Item
                        label="Deep Link"
                        name="deepLink"
                      >
                        <Input
                          prefix={<LinkOutlined />}
                          placeholder="https://url"
                          onChange={this.handleDeepLinkChange}
                        />
                      </Form.Item>
                      <Form.Item label="Upload Image" className="image-upload-item">
                        <Upload
                          listType="picture-card"
                          customRequest={this.handleImageUpload}
                          fileList={uploadFileList}
                          onRemove={this.handleRemoveImage}
                          maxCount={1}
                        >
                          {uploadFileList.length >= 1 ? null : (
                            <div className="upload-placeholder">
                              {imageUploading ? <ReloadOutlined spin /> : <PlusOutlined />}
                              <div style={{ marginTop: 8 }}>{imageUploading ? 'Uploading' : 'Image'}</div>
                            </div>
                          )}
                        </Upload>
                      </Form.Item>
                    </div>

                    <Form.Item
                      label="Extra Custom Data (JSON)"
                      name="customData"
                      help="Additional business logic for front-end (valid JSON object required)"
                    >
                      <TextArea
                        placeholder='{ "action": "REFRESH_DATA", "params": { "id": 123 } }'
                        rows={3}
                        style={{ fontFamily: 'monospace' }}
                      />
                    </Form.Item>
                  </div>

                  <Divider />

                  <div className="form-section scheduling-section">
                    <div className="section-header">
                      <Title level={5}><ClockCircleOutlined /> Delivery Schedule</Title>
                    </div>
                    <div className="delivery-controls">
                      <Form.Item name="sendNow" valuePropName="checked" className="send-now-switch">
                        <Switch
                          checkedChildren="Send Immediately"
                          unCheckedChildren="Schedule Later"
                        />
                      </Form.Item>

                      <Form.Item shouldUpdate={(prev, curr) => prev.sendNow !== curr.sendNow} noStyle>
                        {({ getFieldValue }) => {
                          const sendNow = getFieldValue('sendNow') !== false;
                          return (
                            <Form.Item
                              name="scheduleAt"
                              rules={sendNow ? [] : [{ required: true, message: 'Pick a scheduled time' }]}
                              className={`schedule-picker ${sendNow ? 'hidden' : ''}`}
                            >
                              <DatePicker
                                showTime
                                style={{ width: '100%' }}
                                placeholder="Select date and time"
                                disabled={sendNow}
                              />
                            </Form.Item>
                          );
                        }}
                      </Form.Item>
                    </div>
                  </div>

                  <div className="form-footer">
                    <Space size="middle">
                      <Button
                        icon={<BugOutlined />}
                        onClick={() => this.setState({ testModalVisible: true })}
                        disabled={submitting}
                      >
                        Send Test
                      </Button>
                      {editingId && (
                        <Button
                          size="large"
                          onClick={this.handleCancelEdit}
                        >
                          Cancel Edit
                        </Button>
                      )}
                      <Button
                        type="primary"
                        size="large"
                        icon={editingId ? <EditOutlined /> : <SendOutlined />}
                        htmlType="submit"
                        loading={submitting}
                        className="submit-btn"
                      >
                        {editingId ? 'Update Campaign' : 'Launch Campaign'}
                      </Button>
                    </Space>
                  </div>
                </Form>
              </div>
            </TabPane>
            <TabPane
              tab={<span><HistoryOutlined />Delivery History</span>}
              key="2"
            >
              <div className="history-container">
                <div className="history-filters">
                  <div className="filter-row">
                    <Search
                      placeholder="Search by title or body..."
                      onSearch={this.handleSearch}
                      style={{ width: 300 }}
                      allowClear
                      prefix={<SearchOutlined />}
                    />
                    <Select
                      placeholder="Status"
                      style={{ width: 120 }}
                      allowClear
                      onChange={(val) => this.handleFilterChange('status', val)}
                      options={[
                        { label: 'Scheduled', value: 'scheduled' },
                        { label: 'Sending', value: 'sending' },
                        { label: 'Sent', value: 'sent' },
                        { label: 'Failed', value: 'failed' },
                      ]}
                    />
                    <Select
                      placeholder="App ID"
                      style={{ width: 120 }}
                      allowClear
                      onChange={(val) => this.handleFilterChange('appId', val)}
                      options={(config.notificationApps || []).map(appId => ({ value: appId, label: appId }))}
                    />
                    <RangePicker
                      onChange={(val) => this.handleFilterChange('range', val)}
                    />
                  </div>
                </div>
                <Table
                  rowKey="id"
                  columns={columns}
                  dataSource={notifications}
                  loading={loading}
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} notifications`
                  }}
                  className="modern-table"
                />
              </div>
            </TabPane>
          </Tabs>
        </Card>

        <Modal
          title={<span><BugOutlined /> Send Test Notification</span>}
          visible={testModalVisible}
          onOk={this.handleTestSend}
          onCancel={() => this.setState({ testModalVisible: false })}
          confirmLoading={testing}
          okText="Send Test Now"
          destroyOnClose
        >
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary">
              Enter a FCM device registration token to send this notification immediately for testing.
            </Text>
          </div>
          <Form layout="vertical">
            <Form.Item label="Device Registration Token" required>
              <Input
                value={testToken}
                onChange={(e) => this.setState({ testToken: e.target.value })}
                placeholder="Paste device token here..."
              />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  userProfile: state.user.userProfile,
  shortenLinks: state.shortenLink.shortenlinks,
});

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      notificationCreate,
      notificationUpdate,
      notificationDelete,
      notificationTest,
      notificationList,
      shortenLinkList,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(Notifications);
